
import { createContext, useContext, useState, ReactNode, useRef, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import useMeasure from 'react-use-measure';
import { RoundedOctagon } from '~/blinko/components/Shape';
import { icons } from '~/blinko/assets/icons/icons';

type ToastType = 'success' | 'error' | 'info'

interface Toast {
  id: number;
  type: ToastType;
  message: string;
}

interface ToastContextProps {
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
  raw: (message: string) => void;
}

const ToastContext = createContext<ToastContextProps | undefined>(undefined);

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [elementRef, bounds] = useMeasure();

  const addToast = (type: ToastType, message: string) => {
    const id = Date.now();
    setToasts((prevToasts) => [...prevToasts, { id, type, message }]);
  };

  const removeToast = (id: number) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
  };

  const success = (message: string) => addToast('success', message);
  const error = (message: string) => addToast('error', message);
  const info = (message: string) => addToast('info', message);

  const value = { success, error, info }

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="fixed top-6 left-0 right-0 z-[60] pointer-events-none">
        <motion.div
          className='w-full flex flex-col items-center space-y-2'
          ref={elementRef}
          initial={{ height: 0 }}
          animate={{ height: bounds.height }}
          transition={{ type: 'spring', damping: 30, stiffness: 500 }}
        >
          <AnimatePresence mode="popLayout">
            {toasts.map((toast) => (
              <motion.div
                key={toast.id}
                layout
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ type: 'spring', damping: 30, stiffness: 500 }}
              >
                <ToastItem toast={toast} removeToast={removeToast} />
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </ToastContext.Provider>
  );
};

const ToastItem = ({ toast, removeToast }: { toast: Toast, removeToast: (id: number) => void }) => {
  const [remainingTime, setRemainingTime] = useState(3000);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number | null>(null);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (startTimeRef.current) {
      const elapsed = Date.now() - startTimeRef.current;
      setRemainingTime((prev) => prev - elapsed);
      startTimeRef.current = null;
    }
  };

  const startTimer = () => {
    startTimeRef.current = Date.now();
    timerRef.current = setTimeout(() => {
      removeToast(toast.id);
    }, remainingTime);
  };

  useEffect(() => {
    startTimer();
    return () => {
      clearTimer();
    };
  }, []);

  const handleMouseEnter = () => {
    clearTimer();
  };

  const handleMouseLeave = () => {
    startTimer();
  };

  return (
    <div
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className='pointer-events-auto'
    >
      {toast.type === 'success' ? (
        <Styles.Success message={toast.message} />
      ) : toast.type === 'error' ? (
        <Styles.Error message={toast.message} />
      ) : toast.type === 'info' ? (
        <Styles.Info message={toast.message} onClose={() => removeToast(toast.id)} />
      ): null}
    </div>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

const Styles = {
  Success: ({ message }: { message: string }) => (
    <div>
      <RoundedOctagon
        cornerDiagonal={12}
        cornerRadius={7}
        strokeWidth={1}
        strokeGradientStops={[
        { offset: 0, color: '#FFFFFF73' },
        { offset: 1, color: '#FFFFFF73' },
        ]}
      >
        <div className='absolute inset-0 bg-[#8AA327]' />
        <div className='relative px-3 py-2'>
          <div className='flex items-center gap-2 px-[21.5px]'>
            <img src={icons.toastSuccess} className='w-6 h-6' />
            <div className='text-white text-base font-medium'>
              {message}
            </div>
          </div>
        </div>
      </RoundedOctagon>
    </div>
  ),
  Error: ({ message }: { message: string }) => (
    <div>
      <RoundedOctagon
        cornerDiagonal={12}
        cornerRadius={7}
        strokeWidth={1}
        strokeGradientStops={[
        { offset: 0, color: '#FFFFFF73' },
        { offset: 1, color: '#FFFFFF73' },
        ]}
      >
        <div className='absolute inset-0 bg-[#A65F00]' />
        <div className='relative px-3 py-2'>
          <div className='flex items-center gap-2 px-[21.5px]'>
            <img src={icons.toastSuccess} className='w-6 h-6' />
            <div className='text-white text-base font-medium'>
              {message}
            </div>
          </div>
        </div>
      </RoundedOctagon>
    </div>
  ),
  Info: ({ message, onClose }: { message: string, onClose: () => void }) => (
    <div>
      <RoundedOctagon
        cornerDiagonal={12}
        cornerRadius={7}
        strokeWidth={1}
        strokeGradientStops={[
        { offset: 0, color: '#FFFFFF73' },
        { offset: 1, color: '#FFFFFF73' },
        ]}
      >
        <div className='absolute inset-0 bg-[#E4A500]' />
        <div className='relative px-3 py-2'>
          <div className='flex items-center gap-2 px-[21.5px]'>
            <img src={icons.toastSuccess} className='w-6 h-6' />
            <div className='text-white text-base font-medium'>
              {message}
            </div>
          </div>
        </div>
      </RoundedOctagon>
    </div>
  ),
}